import { createReadStream } from 'node:fs'
import { stat } from 'node:fs/promises'
import { Readable } from 'node:stream'
import { SCOPES, type ApiClient } from './http.js'

export interface PublisherDto {
  id: string
  accountId: string
  name: string
  displayName: string
  isVerified: boolean
  status: string
  signingKeys?: { kid: string; algorithm: string; status: string }[]
}

export interface PackageSummaryDto {
  id: string
  publisherId: string
  publisherName: string
  name: string
  displayName: string
  kind: string
  visibility: string
  latestPublishedVersion?: string | null
}

export interface PagedResult<T> {
  data?: T[]
  items?: T[]
  totalCount?: number
}

export interface PackageVersionDto {
  id: string
  packageId: string
  version: string
  status: string
  sizeBytes: number
  treeDigest: string
  isEncrypted: boolean
  hasDeveloperSignature: boolean
  hasMarketplaceSignature: boolean
}

const pageItems = <T>(page: PagedResult<T>): T[] => page.data ?? page.items ?? []

export async function getMyPublisher(api: ApiClient): Promise<PublisherDto | null> {
  const result = await api.request<PublisherDto | null>('GET', '/v1/publishers/me', {
    scopes: [SCOPES.publisherRead],
  })
  return result ?? null
}

export async function requireMyPublisher(api: ApiClient): Promise<PublisherDto> {
  const publisher = await getMyPublisher(api)
  if (!publisher) {
    throw new Error('No publisher profile for this account. Create one with `verentis publisher create --name <name> --display-name <display>`.')
  }
  return publisher
}

export async function findPackageByName(api: ApiClient, publisherId: string, name: string): Promise<PackageSummaryDto | null> {
  const page = await api.request<PagedResult<PackageSummaryDto>>('GET', `/v1/publishers/${publisherId}/packages`, {
    scopes: [SCOPES.packageRead],
    query: { PageNo: 1, PageSize: 100 },
  })
  return pageItems(page).find((pkg) => pkg.name === name) ?? null
}

export async function createPackage(
  api: ApiClient,
  input: { publisherId: string; name: string; displayName: string; kind: string; description?: string; visibility?: 'Public' | 'Private' },
): Promise<string> {
  return api.request<string>('POST', '/v1/packages', {
    scopes: [SCOPES.packageCreate],
    body: {
      publisherId: input.publisherId,
      name: input.name,
      displayName: input.displayName,
      kind: input.kind,
      description: input.description ?? null,
      icon: null,
      categories: [],
      visibility: input.visibility ?? 'Private',
    },
  })
}

export async function uploadVersion(api: ApiClient, packageId: string, vpkgPath: string): Promise<PackageVersionDto> {
  const size = (await stat(vpkgPath)).size
  const stream = Readable.toWeb(createReadStream(vpkgPath)) as unknown as ReadableStream
  const res = await api.requestRaw('POST', `/v1/packages/${packageId}/versions`, {
    scopes: [SCOPES.packagePublish],
    rawBody: stream,
    contentType: 'application/vnd.verentis.package+tar+gzip',
  })
  void size
  return (await res.json()) as PackageVersionDto
}

export async function yankVersion(api: ApiClient, packageId: string, version: string): Promise<void> {
  await api.request('POST', `/v1/packages/${packageId}/versions/${encodeURIComponent(version)}/yank`, {
    scopes: [SCOPES.packagePublish],
  })
}

export async function downloadTarball(api: ApiClient, packageId: string, version: string, workspaceId?: string): Promise<Response> {
  return api.requestRaw('GET', `/v1/packages/${packageId}/versions/${encodeURIComponent(version)}/tarball`, {
    scopes: [SCOPES.packageRead],
    query: { workspaceId },
  })
}

/** Resolves "publisher/name" or plain "name" (relative to the caller's publisher). */
export async function resolvePackageRef(api: ApiClient, ref: string): Promise<{ package: PackageSummaryDto; publisherName: string }> {
  const [publisherName, packageName] = ref.includes('/') ? (ref.split('/', 2) as [string, string]) : [undefined, ref]

  if (!publisherName) {
    const me = await requireMyPublisher(api)
    const pkg = await findPackageByName(api, me.id, packageName)
    if (!pkg) throw new Error(`Package '${packageName}' not found under your publisher '${me.name}'.`)
    return { package: pkg, publisherName: me.name }
  }

  const page = await api.request<PagedResult<PackageSummaryDto>>('GET', '/v1/packages', {
    scopes: [SCOPES.packageReadAll],
    query: { SearchText: packageName, PageNo: 1, PageSize: 100 },
  })
  const pkg = pageItems(page).find((p) => p.name === packageName && p.publisherName === publisherName)
  if (!pkg) throw new Error(`Package '${publisherName}/${packageName}' not found (or not visible to you).`)
  return { package: pkg, publisherName }
}
